// CabinTravel
// Features:
// - Orchestrates the two-stage camera sequence entering the cabin
// - Manages door interactions synced with camera movement
// - Handles transition to InCabin phase and enabling player controls

import { useEffect, useRef } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import gsap from 'gsap'

import { usePhase, PHASES } from '../../store/usePhase'
import { useStore } from '../../store/useStore'
import { useActions } from '../../store/useActions'

const TRAVEL_CONFIG = {
    cameraPosA: new THREE.Vector3(5.223, 2.114, 1.8),
    cameraPosB: new THREE.Vector3(0.536, 2.3, 0.583),
    targetPosA: new THREE.Vector3(-4.904, 0.79, 0.592),
    targetPosB: new THREE.Vector3(-4.691, 1.943, -1.141),
    durA: 4.0,
    durB: 4.0,
    ease: 'power2.inOut',
    delay: 500, // ms
}

export default function CabinTravel({ controlsRef }) {
    const camera = useThree((state) => state.camera)
    const phase = usePhase((state) => state.phase)
    const setPhase = usePhase((state) => state.setPhase)
    const setCharacterControlsEnabled = useStore(
        (state) => state.setCharacterControlsEnabled
    )

    const openDoor = useActions((state) => state.openDoor)
    const closeDoor = useActions((state) => state.closeDoor)
    const hideLogo = useActions((state) => state.hideLogo)
    const normalizeOutside = useActions((state) => state.normalizeOutside)

    const startedRef = useRef(false)
    const delayRef = useRef(null)

    useEffect(() => {
        if (!camera) return

        // Reset state if not in correct phase
        if (phase !== PHASES.enteringCabin) {
            startedRef.current = false
            if (delayRef.current) {
                clearTimeout(delayRef.current)
                delayRef.current = null
            }
            return
        }

        if (startedRef.current) return
        startedRef.current = true

        // Init actions
        hideLogo()
        normalizeOutside()

        // Start Sequence with Delay
        delayRef.current = setTimeout(() => {
            // ---- CAMERA MOVEMENT SEQUENCE ----

            // Stage A: Approach Door
            gsap.to(camera.position, {
                x: TRAVEL_CONFIG.cameraPosA.x,
                y: TRAVEL_CONFIG.cameraPosA.y,
                z: TRAVEL_CONFIG.cameraPosA.z,
                duration: TRAVEL_CONFIG.durA,
                ease: TRAVEL_CONFIG.ease,
                onComplete: () => {
                    openDoor()

                    // Stage B: Enter Cabin
                    gsap.to(camera.position, {
                        x: TRAVEL_CONFIG.cameraPosB.x,
                        y: TRAVEL_CONFIG.cameraPosB.y,
                        z: TRAVEL_CONFIG.cameraPosB.z,
                        duration: TRAVEL_CONFIG.durB,
                        ease: TRAVEL_CONFIG.ease,
                        onComplete: () => {
                            closeDoor()
                            setCharacterControlsEnabled(true)
                            setPhase(PHASES.inCabin)
                            startedRef.current = false
                        },
                    })
                },
            })

            // ---- TARGET MOVEMENT SEQUENCE ----
            if (controlsRef?.current) {
                // Target A
                gsap.to(controlsRef.current.target, {
                    x: TRAVEL_CONFIG.targetPosA.x,
                    y: TRAVEL_CONFIG.targetPosA.y,
                    z: TRAVEL_CONFIG.targetPosA.z,
                    duration: TRAVEL_CONFIG.durA,
                    ease: TRAVEL_CONFIG.ease,
                    onUpdate: () => controlsRef.current?.update(),
                    onComplete: () => {
                        // Target B
                        gsap.to(controlsRef.current.target, {
                            x: TRAVEL_CONFIG.targetPosB.x,
                            y: TRAVEL_CONFIG.targetPosB.y,
                            z: TRAVEL_CONFIG.targetPosB.z,
                            duration: TRAVEL_CONFIG.durB,
                            ease: TRAVEL_CONFIG.ease,
                            onUpdate: () => controlsRef.current?.update(),
                        })
                    },
                })
            }
        }, TRAVEL_CONFIG.delay)

        return () => {
            if (delayRef.current) {
                clearTimeout(delayRef.current)
                delayRef.current = null
            }
        }
    }, [
        phase,
        camera,
        controlsRef,
        setPhase,
        openDoor,
        closeDoor,
        hideLogo,
        normalizeOutside,
        setCharacterControlsEnabled,
    ])

    return null
}
